/**
 * The first recovery step: the paragraphs we last read, if they still exist
 * and still read like the redline's text. Word keeps `uniqueLocalId` across
 * ordinary edits, so a bookmark deleted by a cut-and-paste of its text is
 * usually found again here without any search.
 */
import type { AnchorMemory } from "../anchor-memory";
import type { CandidateScorer } from "./candidate-scorer";
import type { RecoveryContext, RecoveryStep } from "./recovery-chain";
import { RecoveryResult } from "./recovery-result";
import type { WordParagraph } from "../word-document";

export class ParagraphIdStep implements RecoveryStep {
    readonly tier = "paragraph-id";
    readonly triggers = "any";

    constructor(private readonly scorer: CandidateScorer) {}

    async recover({ body, memory }: RecoveryContext): Promise<RecoveryResult> {
        /**
         * CASES, per remembered id
         *   - the body holds a paragraph under it -> keep that paragraph
         *   - the body holds none                 -> skip this id
         */
        const paragraphs = memory.lastUniqueLocalIds.flatMap((uniqueLocalId) => body.paragraphById(uniqueLocalId) ?? []);
        return this.recoveryFrom(paragraphs, memory);
    }

    /**
     * Whether the paragraphs the remembered ids still name read like the text the redline last
     * covered: the step's one rule, shared by the recovery chain, which finds each paragraph in
     * the body it read once, and by a caller that already holds every paragraph of the body from
     * one list (`AnchorTracker.paragraphsFoundIn`).
     *
     * CASES
     *   - none of the remembered ids names a paragraph    -> not found: no text to score
     *   - their text reads like the last covered text     -> recovered on those paragraphs
     *   - it reads like something else                    -> not found
     *
     * EXAMPLE
     *   remembered span "Customer shall pay within thirty days", the paragraph by id reads the same
     *   -> recovered on it, though its bookmark was deleted
     */
    recoveryFrom(paragraphs: WordParagraph[], memory: AnchorMemory): RecoveryResult {
        /**
         * WHEN no remembered ids still resolve to live paragraphs
         *   CONSEQUENCE
         *     Return notFound(null): this step has no paragraph text to score.
         *
         *   NULL
         *     No paragraph-id match from this step. The caller should try the next recovery step.
         */
        if (paragraphs.length === 0) return RecoveryResult.notFound(null);
        const text = paragraphs.map((paragraph) => paragraph.text).join(" ");
        const similarityToLastSpan = this.scorer.similarity(text, memory.lastSpanText);
        const textMatchesLastSpan = this.scorer.accepts(similarityToLastSpan);
        return textMatchesLastSpan
            ? RecoveryResult.recoveredOnParagraph(paragraphs)
            : RecoveryResult.notFound(null);
    }
}
